import {
  Box,
  Button,
  Typography,
  Paper,
  Tooltip,
  Stack,
  TextField,
  Chip,
  Switch,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import TemplateSectionEditor from "./TemplateSectionEditor";
import MarkdownEditor from "./MarkdownEditor";
import type { DocTemplateFormData, DocTemplateListItem, TemplateSection } from "./templateSerializer";

interface TemplateEditorPanelProps {
  activeTab: number;
  templateListCollapsed: boolean;
  editorWidth: number;
  isDragging: boolean;
  onResizeMouseDown: (e: React.MouseEvent) => void;
  isReadOnlyMode: boolean;
  importAnalyzing: boolean;
  onOpenImportModal: () => void;
  usesSectionEditor: boolean;
  editorMode: "sections" | "markdown";
  onEditorModeChange: (value: "sections" | "markdown") => void;
  searchText: string;
  setSearchText: (v: string) => void;
  templateData: DocTemplateFormData;
  setTemplateData: (updater: (prev: DocTemplateFormData) => DocTemplateFormData) => void;
  selectedOrganization?: string;
  pptSwitch: boolean;
  onSetDefaultPpt: (v: boolean) => void;
  selectedIndex: number | null;
  list: DocTemplateListItem[];
  powerPointFile: File | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onUploadBoxClick: () => void;
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  templateSections: TemplateSection[];
  sectionsCollapseToken: number;
  onSectionsChange: (sections: TemplateSection[]) => void;
  monacoContent: string;
  setMonacoContent: (value: string) => void;
  onMonacoContentChange: (value: string | undefined) => void;
  onEditorMount: (editor: any) => void;
}

const TemplateEditorPanel = ({
  activeTab,
  templateListCollapsed,
  editorWidth,
  isDragging,
  onResizeMouseDown,
  isReadOnlyMode,
  importAnalyzing,
  onOpenImportModal,
  usesSectionEditor,
  editorMode,
  onEditorModeChange,
  searchText,
  setSearchText,
  templateData,
  setTemplateData,
  selectedOrganization,
  pptSwitch,
  onSetDefaultPpt,
  selectedIndex,
  list,
  powerPointFile,
  fileInputRef,
  onUploadBoxClick,
  onFileSelect,
  templateSections,
  sectionsCollapseToken,
  onSectionsChange,
  monacoContent,
  setMonacoContent,
  onMonacoContentChange,
  onEditorMount,
}: TemplateEditorPanelProps) => {
  return (
    <Paper
      className="tour-template-editor"
      elevation={3}
      sx={{
        borderRadius: 2.5,
        width: {
          xs: "100%",
          md: templateListCollapsed ? `${editorWidth}%` : `calc(${editorWidth}% - 8px)`,
        },
        minWidth: {
          xs: "100%",
          md: "300px",
        },
        maxWidth: {
          xs: "100%",
          md: "none",
        },
        flex: {
          xs: "none",
          md: "0 0 auto",
        },
        p: { xs: 1.5, lg: 2 },
        height: {
          xs: "100%",
          lg: "100%",
        },
        minHeight: {
          xs: "100%",
          lg: "100%",
        },
        maxHeight: {
          xs: "100%",
          lg: "100%",
        },
        overflow: "visible",
        display: "flex",
        flexDirection: "column",
        transition: isDragging || !templateListCollapsed ? "none" : "width 0.3s",
        position: "relative",
        mb: { xs: 2, md: 0 },
        pb: { xs: 2, lg: 3 },
        border: isDragging ? "2px solid #1976d2" : "none",
        "&:hover": {
          boxShadow: "0 0 0 1px rgba(25, 118, 210, 0.2)",
        },
        boxSizing: "border-box",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        sx={{ flexShrink: 0, minHeight: 36 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              fontSize: { xs: "1rem", lg: "1.25rem" },
            }}
          >
            Editor
            {isDragging && (
              <Typography
                component="span"
                sx={{
                  ml: 1,
                  fontSize: "0.75rem",
                  color: "#1976d2",
                  fontWeight: "normal",
                  backgroundColor: "rgba(25, 118, 210, 0.1)",
                  px: 0.5,
                  py: 0.2,
                  borderRadius: 1,
                }}
              >
                {Math.round(editorWidth)}%
              </Typography>
            )}
          </Typography>
          {isReadOnlyMode && (
            <Chip
              label="Read Only"
              size="small"
              color="info"
              variant="outlined"
              sx={{
                fontSize: "0.75rem",
                height: 24,
              }}
            />
          )}
        </Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {((activeTab === 0 && editorMode === "sections") || activeTab === 6) && !isReadOnlyMode && (
            <Button
              variant="outlined"
              size="small"
              startIcon={
                importAnalyzing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <UploadFileIcon />
                )
              }
              onClick={onOpenImportModal}
              disabled={importAnalyzing}
              sx={{
                textTransform: "none",
                whiteSpace: "nowrap",
                height: 34,
                minHeight: 34,
                px: 1.5,
              }}
            >
              {activeTab === 6 ? "Upload skill" : "Import"}
            </Button>
          )}
          {activeTab !== 2 && (!usesSectionEditor || editorMode === "markdown") && (
            <Box sx={{ width: 180, minWidth: 120, maxWidth: "100%" }}>
              <input
                type="text"
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                disabled={templateData.templateType === "powerpoint"}
                style={{
                  width: "100%",
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "1px solid #E0E0E0",
                  outline: "none",
                  fontSize: 14,
                  background: templateData.templateType === "powerpoint" ? "#F0F0F0" : "#F9FAFB",
                }}
              />
            </Box>
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          mb: 2,
          backgroundColor: "#f5f5f5",
          padding: 2,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            fullWidth={activeTab !== 2}
            label="Template Name"
            variant="outlined"
            size="small"
            name="templateName"
            value={templateData.templateName}
            disabled={isReadOnlyMode}
            onChange={(e) =>
              setTemplateData((data) => ({
                ...data,
                templateName: e.target.value,
              }))
            }
            sx={{
              flex: activeTab === 2 ? 1 : undefined,
              backgroundColor: "#ffffff",
              "& .MuiInputBase-root": {
                backgroundColor: "#ffffff",
              },
              "& .MuiInputBase-input": {
                fontSize: { xs: 14, lg: 16 },
              },
              opacity: isReadOnlyMode ? 0.7 : 1,
            }}
          />
          {activeTab === 2 && (
            <Tooltip
              title={
                selectedOrganization === "All Organisation"
                  ? "Please select a specific organisation to toggle the default template switch"
                  : ""
              }
              arrow
              placement="top"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "8px 12px",
                  borderRadius: 1,
                  whiteSpace: "nowrap",
                }}
              >
                <Switch
                  checked={pptSwitch}
                  onChange={(e) => onSetDefaultPpt(e.target.checked)}
                  disabled={isReadOnlyMode || selectedOrganization === "All Organisation"}
                  size="small"
                  color="primary"
                  inputProps={{ "aria-label": "ppt-switch" }}
                />
                <Typography variant="body2" sx={{ ml: 0.5, fontSize: "0.875rem" }}>
                  {pptSwitch ? "Default" : "Set Default"}
                </Typography>
              </Box>
            </Tooltip>
          )}
        </Box>
        {(activeTab === 0 || activeTab === 1) && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mt: 1,
              flexWrap: "wrap",
            }}
          >
            <Tooltip
              title={isReadOnlyMode ? "Switch views to preview template content (read-only)" : ""}
              arrow
              placement="top"
            >
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={editorMode}
                  onChange={(_, value) => onEditorModeChange(value)}
                  sx={{
                    height: 34,
                    "& .MuiToggleButton-root": {
                      height: 34,
                      minHeight: 34,
                      py: 0,
                      px: 1.25,
                      textTransform: "none",
                    },
                  }}
                >
                  <ToggleButton value="sections">Section builder</ToggleButton>
                  <ToggleButton value="markdown">Markdown</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Show PowerPoint upload interface when PowerPoint tab is active OR templateType is powerpoint */}
      {activeTab === 2 || templateData.templateType === "powerpoint" ? (
        <Box
          onClick={onUploadBoxClick}
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            minHeight: "300px",
            cursor: isReadOnlyMode ? "not-allowed" : "pointer",
            boxSizing: "border-box",
            opacity: isReadOnlyMode ? 0.7 : 1,
          }}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={onFileSelect}
            accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
            style={{ display: "none" }}
            disabled={isReadOnlyMode}
          />
          <Stack
            spacing={2}
            alignItems="center"
            sx={{
              border: "2px dashed #1976d2",
              borderRadius: 2,
              p: { xs: 3, lg: 6 },
              color: "#1976d2",
              textAlign: "center",
              backgroundColor: "rgba(25, 118, 210, 0.05)",
              width: "90%",
              maxWidth: "500px",
            }}
          >
            <UploadFileIcon sx={{ fontSize: 48, color: "#1976d2" }} />
            <Typography fontWeight="bold" color="#1976d2">
              {powerPointFile
                ? `File selected: ${powerPointFile.name}`
                : selectedIndex !== null && list[selectedIndex]?.templateType === "powerpoint"
                  ? `Template: ${templateData.templateName}`
                  : "No template found!"}
            </Typography>
            <Typography sx={{ color: "#1976d2", fontSize: "0.9rem" }}>
              {isReadOnlyMode
                ? "This template is read-only."
                : powerPointFile ||
                    (selectedIndex !== null && list[selectedIndex]?.templateType === "powerpoint")
                  ? "Click to upload a new .pptx file to replace."
                  : "Please upload the PowerPoint style template file (.pptx)"}
            </Typography>
          </Stack>
        </Box>
      ) : usesSectionEditor ? (
        <Box
          sx={{
            position: "relative",
            flexGrow: 1,
            minHeight: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {importAnalyzing && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                bgcolor: "rgba(255, 255, 255, 0.92)",
                borderRadius: 1,
              }}
            >
              <CircularProgress size={44} />
              <Typography variant="body1" color="text.secondary" fontWeight={500}>
                Analyzing document…
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sections will appear in the editor when ready
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              height: "100%",
              overflow: "auto",
              opacity: isReadOnlyMode ? 0.8 : 1,
              borderRadius: isReadOnlyMode ? 1 : 0,
              border: isReadOnlyMode ? "1px solid #E0E0E0" : "none",
              backgroundColor: isReadOnlyMode ? "#F9FAFB" : "transparent",
              px: 0.5,
              display: editorMode === "sections" ? "block" : "none",
              pointerEvents: importAnalyzing ? "none" : "auto",
            }}
          >
            <TemplateSectionEditor
              sections={templateSections}
              sectionsCollapseKey={
                selectedIndex !== null
                  ? `${list[selectedIndex]?.templateId ?? `idx-${selectedIndex}`}-${sectionsCollapseToken}`
                  : `new-template-${sectionsCollapseToken}`
              }
              onChange={onSectionsChange}
              readOnly={isReadOnlyMode}
            />
          </Box>
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              height: "100%",
              opacity: isReadOnlyMode ? 0.8 : 1,
              borderRadius: isReadOnlyMode ? 1 : 0,
              border: isReadOnlyMode ? "1px solid #E0E0E0" : "none",
              backgroundColor: isReadOnlyMode ? "#F9FAFB" : "transparent",
              display: editorMode === "markdown" ? "block" : "none",
              pointerEvents: importAnalyzing ? "none" : "auto",
            }}
          >
            <MarkdownEditor
              value={monacoContent}
              onChange={onMonacoContentChange}
              readOnly={isReadOnlyMode}
              onMount={onEditorMount}
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            height: "100%",
            opacity: isReadOnlyMode ? 0.8 : 1,
            borderRadius: isReadOnlyMode ? 1 : 0,
            border: isReadOnlyMode ? "1px solid #E0E0E0" : "none",
            backgroundColor: isReadOnlyMode ? "#F9FAFB" : "transparent",
          }}
        >
          <MarkdownEditor
            value={monacoContent}
            onChange={(value) => setMonacoContent(value || "")}
            readOnly={isReadOnlyMode}
            onMount={onEditorMount}
          />
        </Box>
      )}

      {/* Resize Handle - Show in both collapsed and expanded modes */}
      <Tooltip title="Drag to resize editor width" arrow placement="right">
        <Box
          onMouseDown={onResizeMouseDown}
          sx={{
            position: "absolute",
            top: 0,
            right: -6,
            width: 12,
            height: "100%",
            cursor: "col-resize",
            backgroundColor: "transparent",
            zIndex: 10,
            display: { xs: "none", lg: "block" },
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.1)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 4,
              height: 40,
              backgroundColor: isDragging ? "#1976d2" : "#e0e0e0",
              borderRadius: 2,
              transition: "background-color 0.2s ease",
              boxShadow: isDragging ? "0 2px 8px rgba(25, 118, 210, 0.3)" : "none",
            },
            "&:hover::after": {
              backgroundColor: "#1976d2",
              boxShadow: "0 2px 8px rgba(25, 118, 210, 0.2)",
            },
          }}
        />
      </Tooltip>
    </Paper>
  );
};

export default TemplateEditorPanel;
